import { useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { EncryptedPayload } from "~/types/global";
import { SectionEvent } from "~/types/section-streaming";
import { encryptionWorker } from "~/services/workers/encryptionWorker.service";
import { base64ToBuffer, unwrapDekFromUser } from "~/helpers/envelopeCrypto";
import { usePassword } from "~/store/credentials";
import { useReportDetailStore } from "~/store/details/store";
import { useReportProgressStore } from "~/store/reportProgressStore";
import { useSectionStatusStore } from "~/store/sectionStatusStore";
import { findSection } from "~/store/details/utils";
import { extractIncrementalSectionPayload } from "~/helpers/incrementalSectionMerge.helper";
import { persistSection as persistSectionGateway } from "~/services/gateways/section-fetch-more.gateway";
import { resolveReportDekEnvelope } from "~/helpers/reportDekEnvelope.helper";
import {
  markSectionPersisted,
  releaseSectionPersist,
  shouldPersistCompletedSection,
} from "~/helpers/sectionPersist.helper";

/**
 * DORMANT for the partials flow. The live partial save is
 * `useReportPartialSave` (#21220): it encrypts the cumulative snapshot the
 * server already accumulated and overwrites the report through
 * `PUT /reports/insert-report/{id}`, without re-reading any store. This hook
 * stays because it still serves the "Buscar mais dados" flow (#21219), kept in
 * the codebase and tested with its UI entry point hidden. Do not reuse it for
 * partials — reading the merged section back out of the store before encrypting
 * is exactly the timing dependency #21220 removed.
 *
 * Per-section persistence + reconnect re-pull (Feature: redes-sociais-report,
 * task #21220 — closes the seam left by task 7.3, now wired to the real
 * backend endpoint from task #21218).
 *
 * PERSIST ON COMPLETED: on a section's `completed` Section_Event the section
 * content is ENCRYPTED with the report's OWN DEK (unwrapped via
 * `user_wrapped_dek`/`user_dek_iv` — the SAME envelope the rest of the report
 * uses, reused here rather than generating a new DEK or encrypting with the
 * user's raw secret key directly — ADR-013, `docs/vault/07-reference/adrs.md`)
 * and PUT to the dedicated backend endpoint, deduped by `(user_reports_id,
 * section_id)` so duplicate / re-sent `completed` events never double-persist.
 * `started`/`report_section` stay ephemeral preview and never persist (gated
 * in `shouldPersistCompletedSection`).
 *
 * Why the report's DEK, not the user's secret key: a password reset only
 * ever re-wraps the DEK (`docs/vault/06-data-flows/re-encryption.md` — "Não
 * re-encripta dados, apenas re-wraps o DEK") — content encrypted directly
 * with the secret key would silently desync from the rest of the report on
 * the next reset, since nothing would re-encrypt it. Reusing the DEK keeps
 * that invariant intact for section content too.
 *
 * Where the wrap fields come from: `user_wrapped_dek`/`user_dek_iv` are
 * generated once at report CREATION time and are NOT cached anywhere
 * dedicated client-side — they are read back off the matching entry in
 * `reportListStore` (populated by the `["reports","list"]` query, the same
 * store `RenameReportDialog`'s envelope re-encryption already reads from).
 * If that entry isn't loaded yet, persistence is skipped for this event
 * (never falls back to an insecure key) — safe because `repullCurrentPartial`
 * re-syncs from the backend on reconnect/refresh.
 *
 * RECONNECT RE-PULL: on WebSocket reconnect the current partial is restored
 * by re-requesting the report's current persisted state through the existing
 * report-details / list query layer (`repullCurrentPartial`).
 *
 * Security (sensitive domain): the section content is encrypted BEFORE it
 * reaches the persistence boundary (encrypt-before-persist); plaintext section
 * content is never persisted, never written to local/session storage, and never
 * logged. Logs reference work by `section_id` / `user_reports_id` only
 * (identifiers, per correlation Requirement 9.1) — never the payload.
 */

/** Payload handed to the persistence boundary: ALWAYS ciphertext, never plaintext. */
export interface EncryptedSectionPersistInput {
  /** Owning report id (the pending report the section belongs to). */
  userReportsId: string;
  /** Stable section identifier. */
  sectionId: string;
  /** AES-GCM ciphertext + iv of the section content. */
  encrypted: EncryptedPayload;
}

/** Network boundary for persisting one encrypted completed section. */
export type PersistEncryptedSectionFn = (
  input: EncryptedSectionPersistInput,
) => Promise<void>;

export interface UseSectionPersistenceOptions {
  /**
   * Injectable persistence boundary. Defaults to
   * {@link defaultPersistEncryptedSection}. Injected by tests to assert the
   * deduped, encrypt-before-persist contract; replaced by a real gateway call
   * once the backend per-section persist endpoint exists (see seam note above).
   */
  persistEncryptedSection?: PersistEncryptedSectionFn;
}

/**
 * Default persistence boundary — PUTs the already-encrypted section to the
 * dedicated backend endpoint (task #21218). Only ciphertext ever crosses this
 * boundary; it forwards, never inspects.
 */
export const defaultPersistEncryptedSection: PersistEncryptedSectionFn =
  async ({ userReportsId, sectionId, encrypted }) => {
    await persistSectionGateway(userReportsId, sectionId, encrypted);
  };

export function useSectionPersistence(
  options: UseSectionPersistenceOptions = {},
) {
  const persist = options.persistEncryptedSection ?? defaultPersistEncryptedSection;
  const secretKey = usePassword();
  const queryClient = useQueryClient();

  // Per-(report, section) idempotency registry, stable across re-renders.
  // Deliberately in-memory only: the preview is ephemeral (design D3) and no
  // section content is ever written to local/session storage.
  const persistedSectionsRef = useRef<Set<string>>(new Set());

  /**
   * Persist a section's content (encrypted, deduped) ON ITS `completed` event.
   *
   * Safe to call for EVERY section event: it gates internally via
   * `shouldPersistCompletedSection`, so `started`/`report_section` are no-ops.
   */
  const persistCompletedSection = useCallback(
    async (event: SectionEvent): Promise<void> => {
      const registry = persistedSectionsRef.current;
      if (!shouldPersistCompletedSection(event, registry)) {
        console.warn(
          "[useSectionPersistence] Skipping persist — event/dedup gate " +
            `(event=${event.event}, incremental=${event.incremental}, ` +
            `section_id=${event.section_id}, has_section=${event.section !== undefined && event.section !== null})`,
        );
        return;
      }

      // Narrowed by the guard above: completed + non-empty user_reports_id +
      // section content present + not already persisted.
      const userReportsId = event.user_reports_id as string;
      const sectionId = event.section_id;

      // Cannot unwrap the report's DEK without the user's key — skip rather
      // than risk plaintext or an insecure fallback.
      if (!secretKey) {
        console.warn(
          "[useSectionPersistence] Skipping persist — no secretKey in memory " +
            `(section_id=${sectionId}, user_reports_id=${userReportsId})`,
        );
        return;
      }

      // O envelope de DEK é gerado uma vez na CRIAÇÃO do relatório e não é
      // cacheado em lugar dedicado no cliente. A resolução (store da lista →
      // cache de detalhes → fetch ativo de `/get-one-report`) foi extraída para
      // `resolveReportDekEnvelope`, compartilhada com o save de parcial do
      // épico #21124 — os dois reusam o DEK do próprio relatório.
      const dekEnvelope = await resolveReportDekEnvelope(userReportsId, queryClient);
      if (!dekEnvelope) {
        console.warn(
          "[useSectionPersistence] DEK envelope not yet available for " +
            `report ${userReportsId} — skipping persist for this event ` +
            "(reconnect re-pull will re-sync once the list loads).",
        );
        return;
      }
      const { wrappedDek, dekIv } = dekEnvelope;
      console.info(
        `[useSectionPersistence] Persisting section_id=${sectionId} user_reports_id=${userReportsId}`,
      );

      // Mark BEFORE the async work so re-sends during the in-flight window are
      // blocked (mirrors the legacy report-save dedup discipline).
      markSectionPersisted(userReportsId, sectionId, registry);
      try {
        const rawUserKey = base64ToBuffer(secretKey);
        const userKeyBuffer = rawUserKey.buffer.slice(
          rawUserKey.byteOffset,
          rawUserKey.byteOffset + rawUserKey.byteLength,
        ) as ArrayBuffer;

        // Reuse the REPORT'S DEK (never a new one, never the raw secret key
        // directly — ADR-013) — same unwrap primitive the read path uses
        // (useEnvelopeAwareDecryptor.ts).
        const dek = await unwrapDekFromUser(userKeyBuffer, wrappedDek, dekIv);
        const dekBuffer = dek.buffer.slice(
          dek.byteOffset,
          dek.byteOffset + dek.byteLength,
        ) as ArrayBuffer;

        // The backend persist endpoint is a REPLACE upsert (never a merge —
        // see SectionPersistenceService.persist_section), so persisting
        // `event.section` directly would be WRONG for an incremental result:
        // that payload is only THIS fetch-more batch's raw delta, not the
        // cumulative list. Found live (2026-08-13): every incremental persist
        // was silently overwriting the stored section with just the newest
        // batch, discarding the initial preview + any earlier increments.
        //
        // By the time this runs, `PendingReportSockets`'s merge into
        // `useReportDetailStore` has already completed synchronously (it runs
        // in the same `onmessage` tick, before this function's first
        // `await` above yields back to the event loop) — so reading the
        // store here returns the up-to-date, fully merged section.
        let sectionToEncrypt: unknown = event.section;
        if (event.incremental === true) {
          const incrementalPayload = extractIncrementalSectionPayload(event);
          if (incrementalPayload) {
            const { sections, reportType, selectedCombinedFilter } =
              useReportDetailStore.getState().data;
            const mergedSection = findSection(
              sections,
              incrementalPayload.title,
              reportType,
              selectedCombinedFilter,
              incrementalPayload.subsection,
            );
            if (mergedSection) {
              sectionToEncrypt = {
                ...(event.section as Record<string, unknown>),
                data: mergedSection.data,
                data_count: mergedSection.data_count,
              };
            } else {
              console.warn(
                "[useSectionPersistence] Could not locate the merged section in " +
                  `the store for incremental persist (section_id=${sectionId}) — ` +
                  "falling back to this batch's raw delta.",
              );
            }
          }
        }

        // Reuse the existing encrypt primitive (the same AES-GCM worker
        // useEncryption/addNewReportMutation use). Encrypt-before-persist: only
        // ciphertext ever leaves this function.
        const encrypted = (await encryptionWorker.encrypt(
          sectionToEncrypt,
          dekBuffer,
        )) as EncryptedPayload | undefined;
        if (!encrypted || !encrypted.encrypted || !encrypted.iv) {
          throw new Error("section encryption failed");
        }

        await persist({ userReportsId, sectionId, encrypted });
      } catch (err) {
        // Release so a legitimate retry can re-persist (error never suppressed
        // by dedup). Log identifiers only — never section content / PII.
        releaseSectionPersist(userReportsId, sectionId, registry);
        console.warn(
          "[useSectionPersistence] failed to persist section " +
            `(section_id=${sectionId}, user_reports_id=${userReportsId}): ` +
            (err instanceof Error ? err.message : "unknown error"),
        );
      }
    },
    [secretKey, persist, queryClient],
  );

  /**
   * Re-pull the current partial on reconnect (Requirement 6.5).
   *
   * No FE-facing `/partial-result` re-pull endpoint exists yet; the live
   * ephemeral preview is delivered only over the Cross_Replica_Bus → WS. Until
   * that endpoint lands, restore the report's CURRENT persisted state via the
   * existing report-details query so any persisted (completed) sections
   * re-render on reconnect.
   *
   * Deliberately does NOT touch `["reports","list"]`: nothing the reports list
   * cards render (name, status, thumbnails) depends on this re-pull — the
   * streaming progress text/bar reads `useReportProgressStore` directly, not
   * query data — and #21220's real partial flow keeps `hasActiveProgress`
   * true for a report's entire generation window, not just the odd idle
   * reconnect. Invalidating the list here turned every one of THOSE
   * reconnects (which can be frequent while a report streams, not just the
   * ~once-a-minute idle case) into a `GET /reports/get-saved-reports` —
   * found live 2026-08-19 hammering the endpoint nonstop while any report was
   * generating.
   *
   * TODO(redes-sociais-report): when the backend exposes a FE-facing
   * `/partial-result` re-pull endpoint, call it via the gateway here to also
   * restore the in-flight (non-completed) preview.
   */
  const repullCurrentPartial = useCallback(() => {
    if (typeof queryClient?.invalidateQueries !== "function") {
      return;
    }

    // O backend fecha o WS ocioso periodicamente; só refetch se algo estava chegando.
    const hasActiveProgress = Object.keys(useReportProgressStore.getState().progress).length > 0;
    const hasActiveSectionPreview = useSectionStatusStore.getState().order.length > 0;
    if (!hasActiveProgress && !hasActiveSectionPreview) {
      return;
    }

    queryClient.invalidateQueries({
      queryKey: ["reports", "details"],
      exact: false,
    });
  }, [queryClient]);

  return { persistCompletedSection, repullCurrentPartial };
}
